import type { FilterState } from '@/types/nmed/shared-filter-types';
import { parseUserQuery, reconstructQuery } from '@/utils/search-query-parser';
import { push } from 'notivue';

export type ComprehensionCallback = (filter: FilterState) => Promise<FilterState>;

/* ---------------------------------------------------------------------------------------------- */
/*                              TRIGGER-CONSUMER-EMITTER FACTORY                                  */
/* ---------------------------------------------------------------------------------------------- */

export interface TriggerConsumerEmitterConfig {
   /** The filter field name to populate (e.g. 'DischargePermitNumber') */
   filterField: string;
   /** Keywords that activate this comprehension (matched case-insensitively) */
   triggers: string[];
   /** Returns true if the token after the trigger should be consumed */
   consume: (token: string) => boolean;
   /** Transforms a consumed token into one or more filter values */
   emit: (token: string) => string[];
   /** Notification message template. Receives the list of emitted values. */
   notificationMessage: (emittedValues: string[]) => string;
}

/**
 * Creates a ComprehensionCallback from a trigger-consumer-emitter config.
 *
 * The pattern:
 * 1. If the filter field already has values, skip (don't override explicit filters).
 * 2. Find a trigger keyword in the parsed query's `must` terms.
 * 3. Consume consecutive tokens after the trigger that match `consume()`.
 * 4. For each consumed token, call `emit()` to produce filter values.
 * 5. Remove the trigger and consumed tokens from the query, update the filter.
 */
export function createTriggerConsumerEmitter(config: TriggerConsumerEmitterConfig): ComprehensionCallback {
   const { filterField, triggers, consume, emit, notificationMessage } = config;
   const triggerSet = new Set(triggers.map(t => t.toLowerCase()));

   return async (filter) => {
      // Don't override existing filters for this field
      if (filter.searchKeyword.filters[filterField]?.length > 0) {
         return filter;
      }

      const query = parseUserQuery(filter.globalSearchKeyword);
      const triggerIndex = query.must.findIndex(term => !query.quoted.has(term) && triggerSet.has(term.toLowerCase()));
      if (triggerIndex === -1) {
         return filter;
      }

      const emittedValues: string[] = [];
      const consumedIndices = new Set<number>([triggerIndex]);

      for (let i = triggerIndex + 1; i < query.must.length; i++) {
         const term = query.must[i];
         if (!term || query.quoted.has(term))
            continue;
         if (consume(term)) {
            consumedIndices.add(i);
            emittedValues.push(...emit(term));
         }
         else {
            break;
         }
      }

      if (emittedValues.length > 0) {
         filter.searchKeyword.filters[filterField] = emittedValues;

         const newMust = query.must.filter((_, i) => !consumedIndices.has(i));
         filter.globalSearchKeyword = reconstructQuery({ ...query, must: newMust });

         push.info({
            message: notificationMessage(emittedValues),
            duration: 10000,
         });
      }

      return filter;
   };
}

/* ---------------------------------------------------------------------------------------------- */
/*                                  PATTERN MATCH FACTORY                                         */
/* ---------------------------------------------------------------------------------------------- */

export interface PatternComprehensionConfig {
   /** The filter field name to populate (e.g. 'PermitNumber') */
   filterField: string;
   /** Regex that a token must match to be extracted. Applied to unquoted terms only. */
   pattern: RegExp;
   /** Transforms a matched token into one or more filter values */
   emit: (token: string) => string[];
   /** Notification message template. Receives the list of emitted values. */
   notificationMessage: (emittedValues: string[]) => string;
}

/**
 * Creates a ComprehensionCallback that extracts tokens matching a pattern.
 *
 * Unlike trigger-consumer-emitter, there is no trigger keyword — any unquoted
 * token in `must` that matches the pattern is extracted and converted to filter values.
 */
export function createPatternComprehension(config: PatternComprehensionConfig): ComprehensionCallback {
   const { filterField, pattern, emit, notificationMessage } = config;

   return async (filter) => {
      if (filter.searchKeyword.filters[filterField]?.length > 0) {
         return filter;
      }

      const query = parseUserQuery(filter.globalSearchKeyword);
      const emittedValues: string[] = [];
      const matchedIndices = new Set<number>();

      for (let i = 0; i < query.must.length; i++) {
         const term = query.must[i];
         if (!term || query.quoted.has(term))
            continue;
         pattern.lastIndex = 0;
         if (pattern.test(term)) {
            matchedIndices.add(i);
            emittedValues.push(...emit(term));
         }
      }

      if (emittedValues.length > 0) {
         filter.searchKeyword.filters[filterField] = emittedValues;

         const newMust = query.must.filter((_, i) => !matchedIndices.has(i));
         filter.globalSearchKeyword = reconstructQuery({ ...query, must: newMust });

         push.info({
            message: notificationMessage(emittedValues),
            duration: 10000,
         });
      }

      return filter;
   };
}
